import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import DOMPurify from "dompurify";
import * as A from "./lib/api";
import ModelPicker from "./components/ModelPicker";

// ---------- shared helpers ----------
export const H1 = ({ children }) => <h1 className="font-display text-4xl lg:text-5xl tracking-tight mb-2" style={{color:'var(--primary)'}}>{children}</h1>;
export const H2 = ({ children }) => <h2 className="font-display text-2xl mt-8 mb-3" style={{color:'var(--primary)'}}>{children}</h2>;
export const Sub = ({ children }) => <p className="text-sm mb-8" style={{color:'var(--muted)'}}>{children}</p>;
export const Card = ({ children, className = "", ...rest }) => <div className={`card p-5 ${className}`} {...rest}>{children}</div>;

const useLoader = (fn, deps=[]) => {
  const [data, set] = useState(null);
  const [busy, setBusy] = useState(true);
  const reload = useCallback(async () => {
    setBusy(true); try { const r = await fn(); set(r.data); } finally { setBusy(false); }
  }, deps); // eslint-disable-line
  useEffect(() => { reload(); }, [reload]);
  return { data, busy, reload };
};

// ==================== DASHBOARD ====================
export function DashboardPage() {
  const { data, busy } = useLoader(A.dashboard);
  if (busy || !data) return <div>Loading…</div>;
  const tile = (l,v,sub,c='var(--primary)') => (
    <Card><div className="label-caps">{l}</div>
      <div className="font-display text-4xl mt-2" style={{color:c}}>{v}</div>
      {sub && <div className="text-xs mt-1" style={{color:'var(--muted)'}}>{sub}</div>}
    </Card>
  );
  return (
    <div data-testid="dashboard-page">
      <H1>Dashboard 2 · Collective</H1>
      <Sub>The trustees' shared cockpit — a live, collectively-governed view of the Grove. Collective actions require collective approval.</Sub>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        {tile("Entities", data.entities)}
        {tile("Executed Instruments", data.executed_instruments)}
        {tile("Adopted Resolutions", data.adopted_resolutions)}
        {tile("Overdue", data.overdue, "compliance obligations", 'var(--overdue)')}
        {tile("Due Soon", data.due_soon, "within 30 days", 'var(--due-soon)')}
        {tile("Unverified Funding", data.unverified_funding, "sources", 'var(--gold-hover)')}
      </div>
      <div className="grid lg:grid-cols-2 gap-6 mt-8">
        <Card>
          <div className="label-caps mb-2">Upcoming Compliance</div>
          {data.upcoming_compliance.length===0 && <div className="text-sm" style={{color:'var(--muted)'}}>Nothing on the horizon.</div>}
          {data.upcoming_compliance.map(r => (
            <div key={r.id} className="py-2 border-b last:border-b-0 text-sm flex justify-between" style={{borderColor:'var(--border)'}}>
              <div>{r.Title}</div>
              <div className="font-mono">{r.NextDue}</div>
            </div>
          ))}
        </Card>
        <Card>
          <div className="label-caps mb-2">Recent Activity</div>
          {data.recent_activity.slice(0,10).map(a => (
            <div key={a.id} className="py-1.5 text-xs border-b last:border-b-0" style={{borderColor:'var(--border)'}}>
              <span className="font-mono">{(a.timestamp||"").slice(0,16).replace('T',' ')}</span> · <b>{a.actor}</b> — {a.action}
            </div>
          ))}
        </Card>
      </div>
    </div>
  );
}

// ==================== ENTITIES ====================
export function EntitiesPage() {
  const { data, busy, reload } = useLoader(() => A.reg.list("entities"));
  const statuses = ["TO FILE","TO DRAFT","IN DRAFTING","FILED-DORMANT","FORM AT CLOSING","CHARTER PENDING","ACTIVE","DEFERRED"];
  const patch = async (id, field, v) => { await A.reg.patch("entities", id, { [field]: v }); reload(); };
  if (busy) return <div>Loading…</div>;
  return (
    <div data-testid="entities-page">
      <H1>Entities</H1><Sub>The twelve legal bodies that compose the Grove.</Sub>
      <Card className="p-0 overflow-x-auto">
        <table>
          <thead><tr><th>Title</th><th>Role</th><th>Status</th><th>Filing #</th><th>EIN</th></tr></thead>
          <tbody>
          {(data||[]).map(e => (
            <tr key={e.id}>
              <td><div className="font-medium">{e.Title}</div><div className="text-xs" style={{color:'var(--muted)'}}>{e.ShortName}</div></td>
              <td className="text-xs">{e.Role}</td>
              <td><select value={e.Status||""} onChange={ev=>patch(e.id,"Status",ev.target.value)} data-testid={`ent-status-${e.id}`}>
                {statuses.map(s => <option key={s}>{s}</option>)}</select></td>
              <td><input defaultValue={e.FilingNumber||""} onBlur={ev=>patch(e.id,"FilingNumber",ev.target.value)}/></td>
              <td><input defaultValue={e.EIN||""} onBlur={ev=>patch(e.id,"EIN",ev.target.value)} data-testid={`ent-ein-${e.id}`}/></td>
            </tr>
          ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
}

// ==================== INSTRUMENTS ====================
export function InstrumentsPage() {
  const { data, busy, reload } = useLoader(() => A.reg.list("instruments"));
  const [phase, setPhase] = useState("");
  const [gateInst, setGateInst] = useState(null);
  const [ack, setAck] = useState("");
  const statuses = ["Draft","Internal Review","Counsel Review","Approved","Executed"];

  const change = async (inst, newStatus) => {
    if (newStatus === "Executed") {
      if (inst.CounselGated && !["Counsel Review","Approved"].includes(inst.Status)) {
        setGateInst(inst); return;
      }
      await A.executeInstrument(inst.id, null); reload(); toast.success("Instrument executed");
    } else {
      await A.reg.patch("instruments", inst.id, { Status: newStatus }); reload();
    }
  };
  const confirmGate = async () => {
    try { await A.executeInstrument(gateInst.id, ack); toast.success("Executed with skip-resolution logged"); setGateInst(null); setAck(""); reload(); }
    catch(e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const rows = (data||[]).filter(i => !phase || i.Phase===phase);
  if (busy) return <div>Loading…</div>;
  return (
    <div data-testid="instruments-page">
      <H1>Instruments</H1><Sub>67 legal instruments across four phases. The Counsel Gate is enforced.</Sub>
      <div className="flex gap-3 mb-3">
        <select value={phase} onChange={e=>setPhase(e.target.value)} data-testid="phase-filter" className="max-w-xs">
          <option value="">All phases</option>
          <option>1 — Core</option><option>1-2 — Governance</option><option>RE/Regulated</option><option>3 — Foundation</option>
        </select>
      </div>
      <Card className="p-0 overflow-x-auto">
        <table>
          <thead><tr><th>#</th><th>Title</th><th>Phase</th><th>Status</th><th>Gated</th><th>Executed</th></tr></thead>
          <tbody>
          {rows.map(i => (
            <tr key={i.id}>
              <td className="font-mono">{i.DocNumber}</td>
              <td>{i.Title}</td>
              <td className="text-xs">{i.Phase}</td>
              <td><select value={i.Status||""} onChange={e=>change(i, e.target.value)} data-testid={`inst-status-${i.id}`}>
                {statuses.map(s => <option key={s}>{s}</option>)}</select></td>
              <td>{i.CounselGated ? <span className="chip chip-gold">gated</span> : <span className="chip chip-gray">—</span>}</td>
              <td className="font-mono text-xs">{i.ExecutedDate || "—"}</td>
            </tr>
          ))}
          </tbody>
        </table>
      </Card>
      {gateInst && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50" data-testid="counsel-gate">
          <div className="bg-white max-w-lg p-8 rounded" style={{border:'2px solid var(--overdue)'}}>
            <div className="label-caps" style={{color:'var(--overdue)'}}>Counsel Gate</div>
            <h3 className="font-display text-2xl mt-2 mb-2">Executing without counsel review</h3>
            <p className="text-sm mb-4">This instrument requires counsel review. If you proceed, a <b>skip-resolution</b> will be recorded and the trustees accept the risk. Type the acknowledgment exactly to continue:</p>
            <div className="banner-warning mb-3">Type: <b>WE ACCEPT THE RISK</b></div>
            <input value={ack} onChange={e=>setAck(e.target.value)} data-testid="gate-input" placeholder="type the phrase exactly"/>
            <div className="flex gap-3 mt-4 justify-end">
              <button className="btn-outline" onClick={()=>{setGateInst(null); setAck("");}}>Cancel</button>
              <button className="btn-danger" onClick={confirmGate} data-testid="gate-confirm" disabled={ack !== "WE ACCEPT THE RISK"}>Execute & Log Skip-Resolution</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ==================== COMPLIANCE ====================
export function CompliancePage() {
  const { data: rows, busy, reload } = useLoader(() => A.reg.list("compliance"));
  const { data: rd, reload: rdReload } = useLoader(A.radarStatus);
  const patch = async (id, field, v) => { await A.reg.patch("compliance", id, { [field]: v }); reload(); rdReload(); };
  const sweep = async () => { const r = await A.radarSweep(); toast.success(`Sweep updated ${r.data.updated}`); reload(); rdReload(); };
  const statuses = ["PENDING","SCHEDULED","DONE","OVERDUE","NOT YET ACTIVE","STANDING RULE"];
  const radMap = new Map((rd?.rows||[]).map(r => [r.id, r.radar]));
  if (busy) return <div>Loading…</div>;
  return (
    <div data-testid="compliance-page">
      <H1>Compliance</H1><Sub>24 obligations. Radar highlights the OVERDUE and DUE SOON.</Sub>
      <div className="flex gap-4 items-center mb-3">
        <span className="chip chip-red">{rd?.overdue||0} OVERDUE</span>
        <span className="chip chip-amber">{rd?.due_soon||0} DUE SOON</span>
        <button className="btn-outline ml-auto" onClick={sweep} data-testid="radar-sweep-btn">Run radar sweep</button>
      </div>
      <Card className="p-0 overflow-x-auto">
        <table>
          <thead><tr><th>Radar</th><th>Title</th><th>Frequency</th><th>Next Due</th><th>Owner</th><th>Status</th></tr></thead>
          <tbody>
            {(rows||[]).sort((a,b)=>(a.NextDue||"z").localeCompare(b.NextDue||"z")).map(r => {
              const rad = radMap.get(r.id);
              return (
                <tr key={r.id}>
                  <td>{rad==="OVERDUE" ? <span className="chip chip-red">Overdue</span> : rad==="DUE SOON" ? <span className="chip chip-amber">Due soon</span> : <span className="chip chip-gray">—</span>}</td>
                  <td>{r.Title}</td>
                  <td className="text-xs">{r.Frequency}</td>
                  <td><input type="date" defaultValue={(r.NextDue||"").slice(0,10)} onBlur={e=>patch(r.id,"NextDue",e.target.value)}/></td>
                  <td><input defaultValue={r.Owner||""} onBlur={e=>patch(r.id,"Owner",e.target.value)}/></td>
                  <td><select value={r.Status||""} onChange={e=>patch(r.id,"Status",e.target.value)}>{statuses.map(s=><option key={s}>{s}</option>)}</select></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </Card>
    </div>
  );
}

// ==================== RESOLUTIONS ====================
export function ResolutionsPage() {
  const { data: rows, busy, reload } = useLoader(() => A.reg.list("resolutions"));
  const [f, setF] = useState({ Title: "", Text: "" });
  const add = async () => { if(!f.Title) return; await A.reg.create("resolutions", f); setF({Title:"",Text:""}); toast.success("Resolution drafted"); reload(); };
  const adopt = async id => { if(!window.confirm("Adopt & lock this resolution?")) return; await A.adoptRes(id); toast.success("Adopted & locked"); reload(); };
  if (busy) return <div>Loading…</div>;
  return (
    <div data-testid="resolutions-page">
      <H1>Resolutions</H1><Sub>Auto-numbered. Adopted resolutions lock permanently.</Sub>
      <Card className="mb-6">
        <div className="label-caps mb-2">New Resolution</div>
        <input placeholder="Title" value={f.Title} onChange={e=>setF({...f, Title:e.target.value})} data-testid="res-title"/>
        <textarea rows={4} className="mt-2" placeholder="Text…" value={f.Text} onChange={e=>setF({...f, Text:e.target.value})} data-testid="res-text"/>
        <button className="btn-primary mt-3" onClick={add} data-testid="res-add">Draft resolution</button>
      </Card>
      <Card className="p-0 overflow-x-auto">
        <table>
          <thead><tr><th>#</th><th>Title</th><th>Status</th><th>Created</th><th>Adopted</th><th></th></tr></thead>
          <tbody>
          {(rows||[]).sort((a,b)=>(b.Number||"").localeCompare(a.Number||"")).map(r => (
            <tr key={r.id}>
              <td className="font-mono">{r.Number}</td>
              <td><div className="font-medium">{r.Title}</div><div className="text-xs whitespace-pre-wrap" style={{color:'var(--muted)'}}>{(r.Text||"").slice(0,140)}</div></td>
              <td>{r.Status==="Adopted" ? <span className="chip chip-green">Adopted</span> : <span className="chip chip-gray">Draft</span>}</td>
              <td className="font-mono text-xs">{r.Created}</td>
              <td className="font-mono text-xs">{r.AdoptedOn||"—"}</td>
              <td>{r.Status!=="Adopted" && <button className="btn-outline" onClick={()=>adopt(r.id)} data-testid={`adopt-${r.id}`}>Adopt</button>}</td>
            </tr>
          ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
}

// ==================== FUNDING ====================
export function FundingPage() {
  const { data: rows, busy, reload } = useLoader(() => A.reg.list("funding_sources"));
  const [cat, setCat] = useState(""); const [q, setQ] = useState("");
  const [pg, setPg] = useState(""); const [ssn, setSsn] = useState(""); const [doc, setDoc] = useState("");
  const [expanded, setExpanded] = useState({});
  const toggleVer = async r => { const newv = r.Verification==="VERIFIED"?"UNVERIFIED":"VERIFIED";
    await A.reg.patch("funding_sources", r.id, { Verification: newv, LastVerified: newv==="VERIFIED" ? new Date().toISOString().slice(0,10) : r.LastVerified }); reload(); };
  const cats = [...new Set((rows||[]).map(r=>r.Category))];
  const list = (rows||[]).filter(r => (!cat || r.Category===cat) && (!q || (r.Title||"").toLowerCase().includes(q.toLowerCase()))
    && (!pg || r.RequiresPG===pg) && (!ssn || r.RequiresSSN===ssn) && (!doc || r.DocLevel===doc));
  const reqChip = (v, yes, no) => v==="No" ? <span className="chip chip-green text-xs">{no}</span> : v==="Yes" ? <span className="chip chip-amber text-xs">{yes}</span> : <span className="chip chip-gray text-xs">Varies</span>;
  if (busy) return <div>Loading…</div>;
  return (
    <div data-testid="funding-page">
      <H1>Funding Center</H1><Sub>{(rows||[]).length} sources — from EIN-only Net-30 vendors to non-recourse HUD debt. Reality columns visible. Nothing is guaranteed.</Sub>
      <div className="flex gap-3 mb-3 flex-wrap">
        <input placeholder="Search title…" value={q} onChange={e=>setQ(e.target.value)} className="max-w-xs" data-testid="funding-search"/>
        <select value={cat} onChange={e=>setCat(e.target.value)} className="max-w-xs" data-testid="funding-cat">
          <option value="">All categories</option>{cats.map(c=><option key={c}>{c}</option>)}
        </select>
        <select value={pg} onChange={e=>setPg(e.target.value)} className="max-w-[160px]" data-testid="funding-pg-filter">
          <option value="">PG: any</option><option value="No">No PG</option><option value="Yes">PG required</option><option value="Varies">PG varies</option>
        </select>
        <select value={ssn} onChange={e=>setSsn(e.target.value)} className="max-w-[160px]" data-testid="funding-ssn-filter">
          <option value="">SSN: any</option><option value="No">No SSN</option><option value="Yes">SSN required</option><option value="Varies">SSN varies</option>
        </select>
        <select value={doc} onChange={e=>setDoc(e.target.value)} className="max-w-[170px]" data-testid="funding-doc-filter">
          <option value="">Docs: any</option><option value="None">No documents</option><option value="Light">Light docs</option><option value="Full">Full package</option>
        </select>
      </div>
      <div className="text-xs mb-3" style={{color:'var(--muted)'}}>{list.length} matching · quick paths: <button className="underline" onClick={()=>{setPg("No");setSsn("No");setDoc("None");}} data-testid="quick-nopg">No PG + No SSN + No docs</button> · <button className="underline" onClick={()=>{setPg("");setSsn("");setDoc("");setCat("");setQ("");}}>clear filters</button></div>
      <Card className="p-0 overflow-x-auto">
        <table>
          <thead><tr><th>Title</th><th>Category</th><th>Product</th><th>Max</th><th>PG</th><th>SSN</th><th>Docs</th><th>Verified</th></tr></thead>
          <tbody>
            {list.map(r => (<React.Fragment key={r.id}>
              <tr onClick={()=>setExpanded({...expanded, [r.id]: !expanded[r.id]})} style={{cursor:'pointer'}}>
                <td className="font-medium">{r.Title}{r.IsGrant && <span className="chip chip-gold ml-2">grant</span>}</td>
                <td className="text-xs">{r.Category}</td>
                <td className="text-xs">{r.ProductType}</td>
                <td className="text-xs">{r.Max}</td>
                <td>{reqChip(r.RequiresPG, "PG", "No PG")}</td>
                <td>{reqChip(r.RequiresSSN, "SSN", "No SSN")}</td>
                <td><span className={`chip text-xs ${r.DocLevel==="None"?"chip-green":r.DocLevel==="Light"?"chip-gold":"chip-gray"}`}>{r.DocLevel||"Full"}</span></td>
                <td onClick={e=>e.stopPropagation()}><button className="btn-outline text-xs" onClick={()=>toggleVer(r)} data-testid={`ver-${r.id}`}>{r.Verification||"UNVERIFIED"}</button></td>
              </tr>
              {expanded[r.id] && (
                <tr><td colSpan={8} style={{background:'var(--surface-2)'}}>
                  <div className="grid md:grid-cols-3 gap-4 p-3">
                    <div><div className="label-caps">PG Reality</div><div className="text-sm whitespace-pre-wrap">{r.PGReality||"—"}</div></div>
                    <div><div className="label-caps">Reality Note</div><div className="text-sm whitespace-pre-wrap">{r.RealityNote||"—"}</div></div>
                    <div><div className="label-caps">Best Fit</div><div className="text-sm whitespace-pre-wrap">{r.BestFit||"—"}</div></div>
                  </div>
                </td></tr>
              )}
            </React.Fragment>))}
          </tbody>
        </table>
      </Card>
    </div>
  );
}

// ==================== APPLICATIONS ====================
export function ApplicationsPage() {
  const { data: rows, busy, reload } = useLoader(() => A.reg.list("applications"));
  const { data: ents } = useLoader(() => A.reg.list("entities"));
  const [f, setF] = useState({ Title:"", AppDate:"", Entity:"", Amount:"", PGSigner:"", Result:"Pending" });
  const add = async () => {
    if (!f.PGSigner) return toast.error("PGSigner required (natural person or 'None')");
    try { await A.reg.create("applications", f); setF({Title:"",AppDate:"",Entity:"",Amount:"",PGSigner:"",Result:"Pending"}); reload(); toast.success("Logged"); }
    catch (e) { toast.error(e?.response?.data?.detail); }
  };
  if (busy) return <div>Loading…</div>;
  return (
    <div data-testid="applications-page">
      <H1>Applications</H1><Sub>Log applications BEFORE submission. PG signer is required.</Sub>
      <Card className="mb-6">
        <div className="grid md:grid-cols-2 gap-3">
          <input placeholder="Funding source" value={f.Title} onChange={e=>setF({...f,Title:e.target.value})} data-testid="app-title"/>
          <input type="date" value={f.AppDate} onChange={e=>setF({...f,AppDate:e.target.value})}/>
          <select value={f.Entity} onChange={e=>setF({...f,Entity:e.target.value})}>
            <option value="">Applying entity…</option>
            {(ents||[]).map(e=><option key={e.id} value={e.Title}>{e.Title}</option>)}
          </select>
          <input type="number" placeholder="Amount" value={f.Amount} onChange={e=>setF({...f,Amount:e.target.value})}/>
          <input placeholder="PG signer (natural person or 'None')" value={f.PGSigner} onChange={e=>setF({...f,PGSigner:e.target.value})} data-testid="app-pg"/>
          <select value={f.Result} onChange={e=>setF({...f,Result:e.target.value})}>
            {["Pending","Approved","Denied","Withdrawn"].map(o=><option key={o}>{o}</option>)}
          </select>
        </div>
        <button className="btn-primary mt-3" onClick={add} data-testid="app-log">Log application</button>
      </Card>
      <Card className="p-0 overflow-x-auto">
        <table><thead><tr><th>Title</th><th>Date</th><th>Entity</th><th>Amount</th><th>PG</th><th>Result</th></tr></thead>
        <tbody>{(rows||[]).map(r=>(<tr key={r.id}><td>{r.Title}</td><td className="font-mono text-xs">{r.AppDate}</td><td className="text-xs">{r.Entity}</td><td className="font-mono">{r.Amount}</td><td className="text-xs">{r.PGSigner}</td><td>{r.Result}</td></tr>))}</tbody></table>
      </Card>
    </div>
  );
}

// ==================== INSURANCE & LOANS ====================
export function InsuranceLoansPage() {
  const { data: pols, reload: rp } = useLoader(() => A.reg.list("insurance_policies"));
  const { data: lns, reload: rl } = useLoader(() => A.reg.list("loans"));
  const [p, setP] = useState({ Title:"", Insured:"", Type:"Participating whole life (PUA-heavy)", AnnualPremium:"", Status:"Applied" });
  const [l, setL] = useState({ Title:"", Borrower:"", Principal:"", Rate:"", NoteDate:"", Status:"Current" });
  return (
    <div data-testid="insurance-loans-page">
      <H1>Insurance & Loans</H1><Sub>Rate must ≥ AFR. MEC checked on issue, on premium change, annually.</Sub>
      <div className="banner-warning mb-4">Reminder: MEC status is checked before issue, on every premium change, and annually.</div>
      <H2>Insurance Policies</H2>
      <Card className="mb-4">
        <div className="grid md:grid-cols-2 gap-3">
          <input placeholder="Policy title" value={p.Title} onChange={e=>setP({...p,Title:e.target.value})}/>
          <input placeholder="Insured" value={p.Insured} onChange={e=>setP({...p,Insured:e.target.value})}/>
          <select value={p.Type} onChange={e=>setP({...p,Type:e.target.value})}>
            {["Participating whole life (PUA-heavy)","Buy-sell (cross-purchase)","Key person","Term (bridge)"].map(o=><option key={o}>{o}</option>)}
          </select>
          <input type="number" placeholder="Annual premium" value={p.AnnualPremium} onChange={e=>setP({...p,AnnualPremium:e.target.value})}/>
        </div>
        <button className="btn-primary mt-3" onClick={async()=>{await A.reg.create("insurance_policies", p); setP({Title:"",Insured:"",Type:"Participating whole life (PUA-heavy)",AnnualPremium:"",Status:"Applied"}); rp();}}>Add policy</button>
      </Card>
      <Card className="p-0 mb-6 overflow-x-auto"><table><thead><tr><th>Title</th><th>Insured</th><th>Type</th><th>Premium</th><th>MEC Check</th><th>Status</th></tr></thead>
        <tbody>{(pols||[]).map(x=>(<tr key={x.id}><td>{x.Title}</td><td>{x.Insured}</td><td className="text-xs">{x.Type}</td><td className="font-mono">{x.AnnualPremium}</td>
          <td><input type="date" defaultValue={x.LastMECCheck||""} onBlur={e=>A.reg.patch("insurance_policies",x.id,{LastMECCheck:e.target.value}).then(rp)}/></td>
          <td><select value={x.Status} onChange={e=>A.reg.patch("insurance_policies",x.id,{Status:e.target.value}).then(rp)}>{["In force","Applied","Lapse risk","Surrendered"].map(o=><option key={o}>{o}</option>)}</select></td>
        </tr>))}</tbody></table></Card>
      <H2>Loans</H2>
      <Card className="mb-4">
        <div className="grid md:grid-cols-2 gap-3">
          <input placeholder="Lender/Title" value={l.Title} onChange={e=>setL({...l,Title:e.target.value})}/>
          <input placeholder="Borrower" value={l.Borrower} onChange={e=>setL({...l,Borrower:e.target.value})}/>
          <input type="number" placeholder="Principal" value={l.Principal} onChange={e=>setL({...l,Principal:e.target.value})}/>
          <input placeholder="Rate (≥ AFR)" value={l.Rate} onChange={e=>setL({...l,Rate:e.target.value})}/>
          <input type="date" value={l.NoteDate} onChange={e=>setL({...l,NoteDate:e.target.value})}/>
        </div>
        <button className="btn-primary mt-3" onClick={async()=>{await A.reg.create("loans", l); setL({Title:"",Borrower:"",Principal:"",Rate:"",NoteDate:"",Status:"Current"}); rl();}}>Add loan</button>
      </Card>
      <Card className="p-0 overflow-x-auto"><table><thead><tr><th>Lender</th><th>Borrower</th><th>Principal</th><th>Rate</th><th>Date</th><th>Status</th></tr></thead>
        <tbody>{(lns||[]).map(x=>(<tr key={x.id}><td>{x.Title}</td><td>{x.Borrower}</td><td className="font-mono">{x.Principal}</td><td>{x.Rate}</td><td className="font-mono text-xs">{x.NoteDate}</td>
          <td><select value={x.Status} onChange={e=>A.reg.patch("loans",x.id,{Status:e.target.value}).then(rl)}>{["Current","Repaid","Renewed","Delinquent"].map(o=><option key={o}>{o}</option>)}</select></td>
        </tr>))}</tbody></table></Card>
    </div>
  );
}

// ==================== MEETINGS ====================
export function MeetingsPage() {
  const { data: meetings, reload } = useLoader(() => A.reg.list("meetings"));
  const [f, setF] = useState({ Title:"", MeetingDate:"", Type:"Formal (minuted)", Attendees:"", Status:"In session", RecordingLink:"" });
  const [open, setOpen] = useState(null);
  const [motions, setMotions] = useState([]);
  const [mo, setMo] = useState({ Text:"", MovedBy:"", SecondedBy:"", Vote:"Pending", Ayes:0, Nays:0 });
  const create = async () => { const r = await A.reg.create("meetings", f); setF({...f, Title:"", Attendees:""}); reload(); };
  const openMeeting = async m => { setOpen(m); const r = await fetch(`${A.API}/reg/motions`, { headers: {Authorization:`Bearer ${localStorage.getItem("ehu_token")}`}}).then(r=>r.json()); setMotions(r.filter(x=>x.meeting_id===m.id)); };
  const addMotion = async () => { await A.reg.create("motions", { ...mo, meeting_id: open.id }); openMeeting(open); setMo({Text:"",MovedBy:"",SecondedBy:"",Vote:"Pending",Ayes:0,Nays:0}); };
  const genMinutes = async () => { const r = await A.genMinutes(open.id); toast.success("Minutes generated as Shared draft"); reload(); setOpen(null); };
  return (
    <div data-testid="meetings-page">
      <H1>Meeting Suite</H1><Sub>Convene → move motions → vote → generate minutes.</Sub>
      <Card className="mb-6">
        <div className="grid md:grid-cols-2 gap-3">
          <input placeholder="Meeting title" value={f.Title} onChange={e=>setF({...f,Title:e.target.value})}/>
          <input type="date" value={f.MeetingDate} onChange={e=>setF({...f,MeetingDate:e.target.value})}/>
          <select value={f.Type} onChange={e=>setF({...f,Type:e.target.value})}>{["Formal (minuted)","Brainstorm / working session","Ad-hoc"].map(o=><option key={o}>{o}</option>)}</select>
          <input placeholder="Teams recording link" value={f.RecordingLink} onChange={e=>setF({...f,RecordingLink:e.target.value})}/>
          <textarea rows={2} className="md:col-span-2" placeholder="Attendees (one per line)" value={f.Attendees} onChange={e=>setF({...f,Attendees:e.target.value})}/>
        </div>
        <button className="btn-primary mt-3" onClick={create}>Create meeting</button>
      </Card>
      <Card className="p-0 overflow-x-auto"><table><thead><tr><th>Title</th><th>Date</th><th>Type</th><th>Status</th><th></th></tr></thead>
        <tbody>{(meetings||[]).map(m => (<tr key={m.id}><td>{m.Title}</td><td className="font-mono text-xs">{m.MeetingDate}</td><td className="text-xs">{m.Type}</td><td>{m.Status}</td>
          <td><button className="btn-outline" onClick={()=>openMeeting(m)}>Open</button></td></tr>))}
        </tbody></table></Card>
      {open && (
        <div className="fixed inset-0 bg-black/40 z-50 flex justify-center items-start overflow-auto p-8">
          <div className="bg-white max-w-3xl w-full p-6 rounded" style={{border:'1px solid var(--border)'}}>
            <div className="flex justify-between mb-2"><div><div className="font-display text-xl">{open.Title}</div><div className="text-xs">{open.MeetingDate} · {open.Type}</div></div>
              <div className="flex gap-2"><button className="btn-outline" onClick={()=>setOpen(null)}>Close</button><button className="btn-gold" onClick={genMinutes}>Generate minutes</button></div></div>
            <div className="mt-4 p-3 rounded" style={{background:'var(--surface-2)'}}>
              <div className="label-caps mb-2">Add motion</div>
              <textarea rows={2} placeholder="Motion text…" value={mo.Text} onChange={e=>setMo({...mo,Text:e.target.value})}/>
              <div className="grid grid-cols-2 gap-2 mt-2">
                <input placeholder="Moved by" value={mo.MovedBy} onChange={e=>setMo({...mo,MovedBy:e.target.value})}/>
                <input placeholder="Seconded by" value={mo.SecondedBy} onChange={e=>setMo({...mo,SecondedBy:e.target.value})}/>
                <select value={mo.Vote} onChange={e=>setMo({...mo,Vote:e.target.value})}>{["Pending","Carried unanimously","Carried","Failed","Tabled"].map(o=><option key={o}>{o}</option>)}</select>
                <div className="grid grid-cols-2 gap-2"><input type="number" placeholder="Ayes" value={mo.Ayes} onChange={e=>setMo({...mo,Ayes:+e.target.value})}/><input type="number" placeholder="Nays" value={mo.Nays} onChange={e=>setMo({...mo,Nays:+e.target.value})}/></div>
              </div>
              <button className="btn-primary mt-2" onClick={addMotion}>Add motion</button>
            </div>
            <div className="mt-4">
              <div className="label-caps mb-2">Motions ({motions.length})</div>
              {motions.map(m => (<div key={m.id} className="py-2 border-b" style={{borderColor:'var(--border)'}}>
                <div className="text-sm">{m.Text}</div>
                <div className="text-xs" style={{color:'var(--muted)'}}>Moved: {m.MovedBy} · Seconded: {m.SecondedBy} · Vote: {m.Vote} ({m.Ayes}/{m.Nays})</div>
              </div>))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ==================== INSTRUMENT STUDIO ====================
export function StudioPage() {
  const { data: lib } = useLoader(A.libraryList);
  const { data: drafts, reload } = useLoader(A.draftsList);
  const nav = useNavigate();
  const startFrom = async (l) => {
    const r = await A.libraryGet(l.id);
    const d = await A.draftCreate({ Title: `Draft — ${l.title}`, Body: r.data.text, TemplateKey: r.data.key, Visibility: "Private" });
    nav(`/studio/${d.data.id}`);
  };
  const newBlank = async () => {
    const d = await A.draftCreate({ Title: "Untitled draft", Body: "", TemplateKey:"", Visibility:"Private" });
    nav(`/studio/${d.data.id}`);
  };
  return (
    <div data-testid="studio-page">
      <H1>Instrument Studio</H1><Sub>16 legal instruments · draft in a Word-like page · export .docx / .pdf.</Sub>
      <div className="banner-warning mb-6">All instruments carry "DRAFT FOR ATTORNEY REVIEW." Do not remove it.</div>
      <div className="grid md:grid-cols-2 gap-6">
        <div>
          <H2>Library — 16 instruments</H2>
          <div className="space-y-2">
            {(lib||[]).map(l => (
              <div key={l.id} className="card p-3 flex justify-between items-center">
                <div className="font-medium">{l.title}</div>
                <button className="btn-outline text-xs" onClick={()=>startFrom(l)} data-testid={`start-${l.key}`}>Start draft</button>
              </div>
            ))}
          </div>
        </div>
        <div>
          <div className="flex justify-between items-center"><H2>Your drafts</H2><button className="btn-gold" onClick={newBlank}>New blank</button></div>
          <div className="space-y-2">
            {(drafts||[]).map(d => (
              <div key={d.id} className="card p-3 flex justify-between items-center">
                <div>
                  <div className="font-medium">{d.Title}</div>
                  <div className="text-xs" style={{color:'var(--muted)'}}>{d.Visibility} · {d.Author} · {(d.UpdatedAt||"").slice(0,16)}</div>
                </div>
                <Link className="btn-outline text-xs" to={`/studio/${d.id}`}>Open</Link>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export function StudioEditor() {
  const { id } = useParams();
  const [doc, setDoc] = useState(null);
  const [body, setBody] = useState("");
  const [comment, setComment] = useState("");
  const [versions, setVersions] = useState([]);
  const [showVers, setShowVers] = useState(false);
  const load = async () => { const r = await A.draftGet(id); setDoc(r.data); setBody(r.data.Body||""); };
  const loadVersions = async () => { const r = await A.api.get(`/drafts/${id}/versions`); setVersions(r.data); };
  useEffect(()=>{ load(); }, [id]); // eslint-disable-line
  const save = async () => { await A.draftSave(id, { Title: doc.Title, Body: body, Visibility: doc.Visibility }); toast.success("Saved"); load(); if(showVers) loadVersions(); };
  const toDocx = async () => { const r = await A.exportDocx({ title: doc.Title, body }); A.saveBlob(r.data, `${doc.Title}.docx`); };
  const toPdf = async () => { const r = await A.exportPdf({ title: doc.Title, body }); A.saveBlob(r.data, `${doc.Title}.pdf`); };
  const addComment = async () => { if(!comment) return; await A.draftComment(id, comment); setComment(""); load(); };
  const restore = async (vid) => { if(!window.confirm("Restore this version? A snapshot of the current text will be saved first.")) return;
    await A.api.post(`/drafts/${id}/restore/${vid}`); await load(); await loadVersions(); toast.success("Restored"); };
  const openVersions = async () => { setShowVers(true); await loadVersions(); };
  if (!doc) return <div>Loading…</div>;
  return (
    <div data-testid="studio-editor">
      <div className="flex justify-between items-center mb-4">
        <input className="max-w-md font-display text-2xl" value={doc.Title} onChange={e=>setDoc({...doc, Title:e.target.value})}/>
        <div className="flex gap-2">
          <select value={doc.Visibility} onChange={e=>setDoc({...doc, Visibility:e.target.value})}><option>Private</option><option>Shared</option></select>
          <button className="btn-outline" onClick={save} data-testid="editor-save">Save</button>
          <button className="btn-outline" onClick={openVersions} data-testid="editor-versions">History</button>
          <button className="btn-outline" onClick={toDocx} data-testid="export-docx">.docx</button>
          <button className="btn-outline" onClick={toPdf} data-testid="export-pdf">.pdf</button>
        </div>
      </div>
      <div className="banner-warning mb-4 text-xs">
        Formatting cheatsheet: <code># Heading 1</code> · <code>## Heading 2</code> · <code>### Heading 3</code> · <code>- bullet</code> · <code>1. numbered</code> · <code>---</code> for page break · <code>[signature: Name, Title]</code>. Both DOCX and PDF exports respect these.
      </div>
      <div className="grid lg:grid-cols-[1fr_320px] gap-6">
        <div style={{background:'var(--surface-2)', padding:'2rem', borderRadius:'6px'}}>
          <div className="paper" contentEditable suppressContentEditableWarning
            onBlur={e=>setBody(e.currentTarget.innerText)} data-testid="editor-paper">
            {body}
          </div>
        </div>
        <div>
          <Card>
            <div className="label-caps mb-2">Comments {doc.Visibility==="Shared" ? "" : "(shared drafts only)"}</div>
            {doc.Visibility==="Shared" ? (<>
              <textarea rows={2} value={comment} onChange={e=>setComment(e.target.value)} placeholder="Leave a note…"/>
              <button className="btn-outline mt-2 text-xs" onClick={addComment}>Post</button>
              <div className="mt-4 space-y-2">
                {(doc.comments||[]).map(c => (<div key={c.id} className="p-2 border rounded text-xs" style={{borderColor:'var(--border)'}}>
                  <div className="font-medium">{c.Author}</div><div>{c.Text}</div>
                  <div className="mt-1 font-mono" style={{color:'var(--muted)'}}>{(c.CreatedAt||"").slice(0,16)}</div>
                </div>))}
              </div>
            </>) : <div className="text-xs" style={{color:'var(--muted)'}}>Make it Shared to collaborate.</div>}
          </Card>
        </div>
      </div>
      {showVers && (
        <div className="fixed inset-0 bg-black/40 z-50 flex justify-end" onClick={()=>setShowVers(false)}>
          <div className="bg-white w-[520px] h-full overflow-y-auto p-6" onClick={e=>e.stopPropagation()}>
            <div className="flex justify-between items-center mb-4"><div className="font-display text-xl">Version history</div><button onClick={()=>setShowVers(false)}>×</button></div>
            {versions.length===0 && <div className="text-sm" style={{color:'var(--muted)'}}>No prior versions yet — a snapshot is created each time the body changes.</div>}
            {versions.map(v => (
              <div key={v.id} className="border-b py-3" style={{borderColor:'var(--border)'}}>
                <div className="text-xs font-mono">{(v.SavedAt||"").slice(0,16).replace('T',' ')}</div>
                <div className="text-xs" style={{color:'var(--muted)'}}>{v.Author}</div>
                <div className="text-xs mt-1 truncate">{(v.Body||"").slice(0,120)}…</div>
                <button className="btn-outline text-xs mt-2" onClick={()=>restore(v.id)}>Restore this version</button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ==================== ACADEMY ====================
const ProgressBar = ({ pct }) => (
  <div className="h-1.5 rounded-full overflow-hidden" style={{background:'var(--surface-2, #EDE8DE)'}}>
    <div className="h-full rounded-full transition-all" style={{width:`${pct}%`, background: pct===100 ? 'var(--green, #2F855A)' : 'var(--gold)'}}/>
  </div>
);

export function AcademyPage() {
  const { data, busy } = useLoader(A.courses);
  const [programs, setPrograms] = useState([]);
  const [status, setStatus] = useState(null);
  useEffect(() => {
    A.academyPrograms().then(r => setPrograms(r.data || [])).catch(() => {});
    A.academyStatus().then(r => setStatus(r.data)).catch(() => {});
  }, []);
  const downloadCert = async (e, c) => {
    e.preventDefault();
    const r = await A.courseCertificate(c.id);
    A.saveBlob(r.data, `Certificate - ${c.title}.pdf`);
    toast.success("Certificate downloaded");
  };
  if (busy) return <div>Loading…</div>;
  const courses = data || [];
  // Group by program (falls back to section for any orphans)
  const grouped = {};
  courses.forEach(c => {
    const key = c.program_id || `section:${c.section || c.tag || "More Courses"}`;
    (grouped[key] = grouped[key] || []).push(c);
  });
  const programsById = Object.fromEntries((programs || []).map(p => [p.id, p]));
  const orderedKeys = [
    // Show ALL programs in sort order (even empty ones — surfaced with a placeholder).
    ...(programs || []).sort((a,b)=> (a.sort_order||0)-(b.sort_order||0)).map(p => p.id),
    // Then any orphan sections not tied to a program.
    ...Object.keys(grouped).filter(k => !programsById[k]),
  ];
  const CourseCard = (c) => (
    <Link key={c.id} to={`/academy/${c.id}`} className="card p-5 block hover:-translate-y-0.5 transition-transform" data-testid={`course-card-${c.key}`}>
      <div className="flex justify-between items-start mb-3">
        <div className="chip chip-gold text-xs">{c.section || c.tag}</div>
        {c.certificate_ready && <span className="chip chip-green text-xs">Completed</span>}
      </div>
      <div className="font-display text-xl mb-2">{c.title}</div>
      {c.source_review_date && <div className="text-xs mb-2" style={{color:'var(--muted)'}}>Source-reviewed: {c.source_review_date}</div>}
      <ProgressBar pct={c.pct}/>
      <div className="flex justify-between items-center mt-2">
        <div className="text-xs font-mono">{c.completed}/{c.lesson_count} lessons · {c.pct}%</div>
        {c.certificate_ready
          ? <button className="btn-outline text-xs" onClick={(e)=>downloadCert(e,c)} data-testid={`cert-download-${c.key}`}>Certificate</button>
          : c.completed > 0 && <span className="text-xs" style={{color:'var(--gold-hover)'}}>Resume →</span>}
      </div>
    </Link>
  );
  return (
    <div data-testid="academy-page">
      <H1>Academy</H1>
      <Sub>{courses.length} courses across {orderedKeys.length} programs. Lessons unlock in sequence; each lesson requires an 80% pass or a documented review-and-acknowledgment. Finished courses issue an internal certificate.</Sub>

      {status && (
        <div className="card p-4 mb-4" data-testid="academy-status-panel">
          <div className="flex items-center justify-between mb-2">
            <div>
              <div className="label-caps">Academic status</div>
              <div className="font-display text-lg">{status.status_label}</div>
            </div>
            <span className="chip chip-gold text-xs">Effective {status.effective_date}</span>
          </div>
          <div className="text-sm" style={{color:'var(--muted)'}}>{status.limitations}</div>
          <div className="text-xs mt-2" style={{color:'var(--muted)'}}>Next status review: {status.next_review_date} · Credit type: {status.credit_type} · Default passing score: {status.passing_score_default}%</div>
        </div>
      )}

      <div className="banner-warning mb-6">Internal educational material only — not legal, tax, medical, licensing, or accreditation advice. Completion does not create legal, fiduciary, ownership, employment, voting, or investment authority. Professional review required.</div>

      {orderedKeys.map(key => {
        const prog = programsById[key];
        const label = prog ? prog.title : key.replace(/^section:/, "");
        const purpose = prog?.purpose;
        const rows = grouped[key] || [];
        return (
          <div key={key} className="mb-8" data-testid={`academy-program-${(prog?.code || label).replace(/[^a-zA-Z]+/g,'-')}`}>
            <div className="flex justify-between items-baseline mb-2">
              <H2>{label}</H2>
              {prog && <span className="text-xs font-mono" style={{color:'var(--muted)'}}>{prog.credential}</span>}
            </div>
            {purpose && <div className="text-sm mb-4" style={{color:'var(--muted)'}}>{purpose}</div>}
            {rows.length > 0 ? (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
                {rows.map(CourseCard)}
              </div>
            ) : (
              <div className="card p-4 text-sm" style={{color:'var(--muted)'}} data-testid={`academy-program-empty-${(prog?.code || label).replace(/[^a-zA-Z]+/g,'-')}`}>
                No courses currently assigned to this program. Content is authored and reviewed on the Academy source-maintenance cadence.
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

export function CoursePage() {
  const { id } = useParams();
  const [d, setD] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [tab, setTab] = useState("lessons");
  useEffect(() => { setNotFound(false); A.courseDetail(id).then(r=>setD(r.data)).catch(()=>setNotFound(true)); }, [id]);
  const downloadCert = async () => {
    const r = await A.courseCertificate(id);
    A.saveBlob(r.data, `Certificate - ${d.course.title}.pdf`);
    toast.success("Certificate downloaded");
  };
  if (notFound) return (
    <div data-testid="course-not-found">
      <H1>Course not found</H1><Sub>This course does not exist or the link is stale.</Sub>
      <Link to="/academy" className="btn-outline">← Back to Academy</Link>
    </div>
  );
  if (!d) return <div>Loading…</div>;
  const next = d.lessons.find(l => !l.progress?.completed && !l.locked);
  const c = d.course;
  const cleanNotice = DOMPurify.sanitize(c.notice_html || "");
  const cleanOutcomes = DOMPurify.sanitize(c.learning_outcomes_html || "");
  const cleanPracticum = DOMPurify.sanitize(c.practicum_html || "");
  const cleanFinal = DOMPurify.sanitize(c.final_assessment_html || "");
  const cleanChecklist = DOMPurify.sanitize(c.operational_checklist_html || "");
  const cleanCompletion = DOMPurify.sanitize(c.completion_statement_html || "");
  const cleanAccept = DOMPurify.sanitize(c.acceptance_criteria_html || "");
  const cleanSources = DOMPurify.sanitize(c.source_maintenance_html || "");
  const tabs = [
    ["lessons", "Lessons"],
    ["overview", "Overview"],
    (cleanOutcomes ? ["outcomes", "Learning Outcomes"] : null),
    (cleanPracticum ? ["practicum", "Practicum"] : null),
    (cleanFinal ? ["final", "Final Assessment"] : null),
    (cleanChecklist ? ["checklist", "Operational Checklist"] : null),
    (cleanSources ? ["sources", "Sources"] : null),
    (c.alt_text_registry?.length ? ["accessibility", "Accessibility"] : null),
  ].filter(Boolean);
  return (
    <div data-testid="course-page">
      <div className="flex justify-between items-start mb-4">
        <div>
          <div className="chip chip-gold mb-2 text-xs">{c.section || c.tag}</div>
          <H1>{c.title}</H1>
          <div className="text-sm mt-1 space-x-3" style={{color:'var(--muted)'}}>
            <span>Passing score: {c.passing_score || 80}%</span>
            {c.source_review_date && <span>· Source-reviewed: {c.source_review_date}</span>}
            {c.credit_type && <span>· {c.credit_type.replace(/_/g,' ')}</span>}
          </div>
        </div>
        {c.certificate_ready && <button className="btn-primary" onClick={downloadCert} data-testid="course-cert-btn">Download certificate</button>}
      </div>

      <div className="mb-4 max-w-md"><ProgressBar pct={c.pct}/><div className="text-xs font-mono mt-1">{c.pct}% complete · {d.lessons.filter(l=>l.progress?.completed).length}/{d.lessons.length} lessons</div></div>

      {next && <Link to={`/academy/lesson/${next.id}`} className="btn-gold inline-block mb-4" data-testid="resume-lesson-btn">Resume: {next.title}</Link>}

      <div className="flex gap-1 mb-4 border-b overflow-x-auto" style={{borderColor:'var(--border)'}} data-testid="course-tabs">
        {tabs.map(([k, label]) => (
          <button key={k} onClick={()=>setTab(k)} className={`px-3 py-2 text-sm ${tab===k?'border-b-2 font-medium':''}`}
            style={{borderColor: tab===k?'var(--gold)':'transparent', color: tab===k?'var(--gold)':'var(--muted)'}}
            data-testid={`course-tab-${k}`}>{label}</button>
        ))}
      </div>

      {tab === "lessons" && <div className="space-y-2" data-testid="course-tab-lessons-panel">
        {d.lessons.map((l, i) => l.locked ? (
          <div key={l.id} className="card p-4 flex justify-between items-center opacity-55 cursor-not-allowed" data-testid={`lesson-row-locked-${i}`}>
            <div><span className="font-mono text-xs mr-3">{String(i+1).padStart(2,'0')}</span>{l.title}</div>
            <span className="chip chip-gray">🔒 Locked</span>
          </div>
        ) : (
          <Link key={l.id} to={`/academy/lesson/${l.id}`} className="card p-4 flex justify-between items-center hover:bg-[color:var(--surface-2)]" data-testid={`lesson-row-${i}`}>
            <div><span className="font-mono text-xs mr-3">{String(i+1).padStart(2,'0')}</span>{l.title}</div>
            <div>{l.progress?.completed ? <span className="chip chip-green">Complete</span> : <span className="chip chip-gold">Open</span>}</div>
          </Link>
        ))}
        <div className="text-xs mt-4" style={{color:'var(--muted)'}}>Lessons unlock in order. Each lesson requires an 80% quiz pass or a documented review-and-acknowledgment to complete.</div>
      </div>}

      {tab === "overview" && <div data-testid="course-tab-overview-panel">
        {cleanNotice && <Card className="mb-4"><div className="label-caps mb-2 text-amber-800">Course Notice</div><div className="prose prose-sm max-w-none" dangerouslySetInnerHTML={{__html: cleanNotice}}/></Card>}
        {cleanCompletion && <Card><div className="label-caps mb-2">Course Completion Statement</div><div className="prose prose-sm max-w-none" dangerouslySetInnerHTML={{__html: cleanCompletion}}/></Card>}
      </div>}
      {tab === "outcomes" && <div className="prose max-w-none" data-testid="course-tab-outcomes-panel" dangerouslySetInnerHTML={{__html: cleanOutcomes}}/>}
      {tab === "practicum" && <div className="prose max-w-none" data-testid="course-tab-practicum-panel" dangerouslySetInnerHTML={{__html: cleanPracticum}}/>}
      {tab === "final" && <div className="prose max-w-none" data-testid="course-tab-final-panel" dangerouslySetInnerHTML={{__html: cleanFinal}}/>}
      {tab === "checklist" && <div className="prose max-w-none" data-testid="course-tab-checklist-panel" dangerouslySetInnerHTML={{__html: cleanChecklist}}/>}
      {tab === "sources" && <div data-testid="course-tab-sources-panel">
        <div className="prose max-w-none" dangerouslySetInnerHTML={{__html: cleanSources}}/>
        {cleanAccept && <div className="mt-6"><H2>Acceptance Criteria</H2><div className="prose max-w-none" dangerouslySetInnerHTML={{__html: cleanAccept}}/></div>}
      </div>}
      {tab === "accessibility" && <div data-testid="course-tab-accessibility-panel">
        <div className="label-caps mb-2">Alt Text Registry</div>
        <div className="text-xs mb-3" style={{color:'var(--muted)'}}>Descriptive alt text used across recommended course visuals to preserve meaning for screen-reader users.</div>
        <div className="space-y-2">
          {(c.alt_text_registry||[]).map((a,i) => (
            <Card key={i}>
              <div className="text-sm font-medium">{a.label}</div>
              <div className="text-sm mt-1" style={{color:'var(--muted)'}}>{a.alt_text}</div>
            </Card>
          ))}
        </div>
      </div>}
    </div>
  );
}

function Flashcards({ cards }) {
  const [i, setI] = useState(0);
  const [flip, setFlip] = useState(false);
  if (!cards?.length) return null;
  const c = cards[i];
  return (
    <div className="mt-8">
      <H2>Flashcards</H2>
      <div className="card p-8 text-center cursor-pointer select-none min-h-[140px] flex flex-col justify-center"
        onClick={()=>setFlip(f=>!f)} data-testid="flashcard">
        <div className="label-caps mb-2">{flip ? "Answer" : "Term"} · tap to flip</div>
        <div className={flip ? "text-base font-serif-body" : "font-display text-xl"}>{flip ? c.b : c.f}</div>
      </div>
      <div className="flex justify-between items-center mt-3">
        <button className="btn-outline text-xs" onClick={()=>{setI(x=>(x-1+cards.length)%cards.length); setFlip(false);}} data-testid="flashcard-prev">← Prev</button>
        <span className="text-xs font-mono">{i+1} / {cards.length}</span>
        <button className="btn-outline text-xs" onClick={()=>{setI(x=>(x+1)%cards.length); setFlip(false);}} data-testid="flashcard-next">Next →</button>
      </div>
    </div>
  );
}

function Scenarios({ scenarios }) {
  const [picked, setPicked] = useState({});
  if (!scenarios?.length) return null;
  return (
    <div className="mt-8">
      <H2>Scenario Drills</H2>
      {scenarios.map((s, si) => {
        const sel = picked[si];
        return (
          <Card key={si} className="mb-4" >
            <div className="label-caps mb-2">What would you do?</div>
            <div className="font-medium mb-3">{s.s}</div>
            {(s.o||[]).map((o, oi) => {
              let cls = "border rounded px-3 py-2 mb-2 cursor-pointer text-sm transition-colors";
              if (sel !== undefined) {
                if (oi === s.a) cls += " border-green-700 bg-green-50";
                else if (oi === sel) cls += " border-red-700 bg-red-50";
                else cls += " opacity-60";
              } else cls += " hover:bg-[color:var(--surface-2)]";
              return <div key={oi} className={cls} style={{borderColor: sel===undefined ? 'var(--border)' : undefined}}
                onClick={()=> sel===undefined && setPicked(p=>({...p,[si]:oi}))} data-testid={`scenario-${si}-option-${oi}`}>{o}</div>;
            })}
            {sel !== undefined && (
              <div className={`mt-2 p-3 rounded text-sm ${sel===s.a ? 'bg-green-50' : 'bg-amber-50'}`} data-testid={`scenario-${si}-explain`}>
                <b>{sel===s.a ? "Correct." : "Not quite."}</b> {s.why}
              </div>
            )}
          </Card>
        );
      })}
    </div>
  );
}

export function LessonPage() {
  const { id } = useParams();
  const [d, setD] = useState(null);
  const [locked, setLocked] = useState(false);
  const [ans, setAns] = useState([]);
  const [responses, setResponses] = useState([]);
  const [acked, setAcked] = useState(false);
  const [showAnsStd, setShowAnsStd] = useState(false);
  const [res, setRes] = useState(null);
  const [user, setUser] = useState(null);
  const [enriching, setEnriching] = useState(false);
  const [enrichModel, setEnrichModel] = useState("claude");
  const cleanTeaching = useMemo(() => DOMPurify.sanitize(d?.lesson?.teaching_html || ""), [d]);
  const cleanBody = useMemo(() => DOMPurify.sanitize(d?.lesson?.body_html || ""), [d]);
  const cleanAnsStd = useMemo(() => DOMPurify.sanitize(d?.lesson?.answer_standard_html || ""), [d]);
  const load = () => {
    setLocked(false);
    A.lessonDetail(id).then(r => {
      setD(r.data);
      setAns(new Array(r.data.quiz.length).fill(-1));
      const aq = r.data.lesson?.assessment_questions || [];
      setResponses(new Array(aq.length).fill(""));
      setAcked(false);
      setShowAnsStd(false);
      setRes(null);
    }).catch(e => { if (e?.response?.status === 423) setLocked(true); });
  };
  useEffect(() => { load(); A.me().then(r=>setUser(r.data)).catch(()=>{}); }, [id]); // eslint-disable-line
  const isOpen = (d?.lesson?.assessment_type === "open_short_answer") || (d?.quiz?.length === 0);
  const submit = async () => {
    const payload = isOpen ? { responses, acknowledged: acked } : { answers: ans };
    const r = await A.submitQuiz(id, payload); setRes(r.data);
    if (r.data.completed) toast.success("Lesson complete — the next lesson is unlocked.");
    else if (isOpen) toast.error("Please answer each question (min. 3 characters) and check the acknowledgment before submitting.");
    else toast.error(`Score ${Math.round(r.data.score*100)}% — you need ${Math.round((r.data.threshold||0.8)*100)}% to pass. Review and retake.`);
    A.lessonDetail(id).then(x=>setD(dd=>({...x.data, quiz: dd.quiz})));
  };
  const enrich = async () => {
    setEnriching(true);
    try { await A.aiEnrichLesson(id, "extras", enrichModel); toast.success("AI generated flashcards & scenarios"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "AI enrichment failed"); }
    finally { setEnriching(false); }
  };
  const [regenTeach, setRegenTeach] = useState(false);
  const regenerateTeaching = async () => {
    setRegenTeach(true);
    try { await A.academyRegenerateTeaching(id); toast.success("Teaching content regenerated"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Regeneration failed"); }
    finally { setRegenTeach(false); }
  };
  if (locked) return (
    <div data-testid="lesson-locked">
      <H1>Lesson locked</H1>
      <Sub>Complete the previous lessons in this course (80% pass or documented review) to unlock this one.</Sub>
      <button className="btn-outline" onClick={()=>window.history.back()}>← Back to course</button>
    </div>
  );
  if (!d) return <div>Loading…</div>;
  const missingExtras = !(d.lesson.flashcards?.length) && !(d.lesson.scenarios?.length);
  const aq = d.lesson.assessment_questions || [];
  return (
    <div data-testid="lesson-page">
      <div className="banner-warning mb-4">Training material only — not legal, tax, medical, or licensing advice. Completion is educational; it does not confer authority.</div>
      <div className="flex justify-between items-start">
        <div>
          {d.course && <div className="label-caps mb-1">Lesson {d.course.position} of {d.course.total}</div>}
          <H1>{d.lesson.title}</H1>
          {d.lesson.source_review_date && <div className="text-xs" style={{color:'var(--muted)'}}>Source-reviewed: {d.lesson.source_review_date}</div>}
        </div>
        {user?.role === "records_officer" && (
          <div className="flex items-center gap-2 flex-wrap justify-end">
            <ModelPicker value={enrichModel} onChange={setEnrichModel} testid="enrich-model-picker" />
            {missingExtras && (
              <button className="btn-outline text-xs" onClick={enrich} disabled={enriching} data-testid="ai-enrich-btn">
                {enriching ? "AI is writing…" : "✦ Generate flashcards & drills"}
              </button>
            )}
            <button className="btn-outline text-xs" onClick={regenerateTeaching} disabled={regenTeach} data-testid="regen-teaching-btn"
              title="Regenerate the long-form teaching body for this lesson using Claude.">
              {regenTeach ? "Rewriting…" : (d.lesson.teaching_html ? "✦ Regenerate teaching" : "✦ Generate teaching")}
            </button>
          </div>
        )}
      </div>
      {cleanTeaching ? (
        <div className="card p-5 mt-4" data-testid="lesson-teaching">
          <div className="label-caps mb-2">Teaching</div>
          <div className="prose max-w-none font-serif-body" dangerouslySetInnerHTML={{__html: cleanTeaching}}/>
          {d.lesson.teaching_depth && <div className="text-xs mt-3" style={{color:'var(--muted)'}}>Depth: {d.lesson.teaching_depth} · v{d.lesson.teaching_version || 1}{d.lesson.teaching_generated_at ? ` · authored ${new Date(d.lesson.teaching_generated_at).toLocaleDateString()}` : ""}</div>}
        </div>
      ) : (
        user?.role === "records_officer" && (
          <div className="banner-warning mt-4" data-testid="teaching-missing-banner">
            This lesson has no long-form teaching content yet. Click <em>Generate teaching</em> above, or open Academy → Teaching Generator to rebuild every lesson in one pass.
          </div>
        )
      )}
      <div className="mt-6 label-caps">Operating Framework</div>
      <div className="prose max-w-none font-serif-body" dangerouslySetInnerHTML={{__html: cleanBody}}/>
      {(d.lesson.key_points||[]).length>0 && <Card className="mt-6"><div className="label-caps mb-2">Key Points</div>
        <ul className="list-disc pl-6 space-y-1 text-sm">{d.lesson.key_points.map((p,i)=>(<li key={i}>{p}</li>))}</ul></Card>}
      <Flashcards cards={d.lesson.flashcards}/>
      <Scenarios scenarios={d.lesson.scenarios}/>

      {isOpen && aq.length > 0 && <div className="mt-8" data-testid="lesson-open-assessment">
        <H2>Lesson Assessment</H2>
        <div className="text-xs mb-3" style={{color:'var(--muted)'}}>Write a concise short answer for each item. Then review the Answer Standard below and confirm your review to complete the lesson. Passing standard: at least 80% of items answered plus your acknowledgment.</div>
        {aq.map((q, qi) => {
          const prompt = typeof q === "string" ? q : q.prompt;
          const modelAns = typeof q === "object" ? q.answer_html : "";
          return (
            <Card key={qi} className="mb-3">
              <div className="font-medium mb-2">{qi+1}. {prompt}</div>
              <textarea
                className="w-full border rounded p-2 text-sm bg-transparent"
                style={{borderColor:'var(--border)', minHeight: '80px'}}
                value={responses[qi] || ""}
                onChange={(e)=>{const r=[...responses]; r[qi]=e.target.value; setResponses(r);}}
                placeholder="Type your short answer…"
                data-testid={`assessment-q${qi}-input`}
              />
              {modelAns && res && (
                <div className="mt-2 text-xs p-2 rounded bg-[color:var(--surface-2)]" data-testid={`assessment-q${qi}-model`}>
                  <span className="label-caps">Model answer</span>
                  <div className="prose prose-sm max-w-none" dangerouslySetInnerHTML={{__html: DOMPurify.sanitize(modelAns)}}/>
                </div>
              )}
            </Card>
          );
        })}
        {cleanAnsStd && (
          <div className="mb-4">
            <button className="btn-outline text-xs mb-2" onClick={()=>setShowAnsStd(v=>!v)} data-testid="toggle-answer-standard">
              {showAnsStd ? "Hide" : "Show"} Answer Standard / Rubric
            </button>
            {showAnsStd && <Card><div className="label-caps mb-2">Answer Standard</div><div className="prose prose-sm max-w-none" dangerouslySetInnerHTML={{__html: cleanAnsStd}}/></Card>}
          </div>
        )}
        <label className="flex items-start gap-2 mb-3 cursor-pointer" data-testid="assessment-ack-label">
          <input type="checkbox" checked={acked} onChange={e=>setAcked(e.target.checked)} data-testid="assessment-ack" className="mt-1"/>
          <span className="text-sm">I have read the full lesson body, drafted an honest short answer for each item, and reviewed the Answer Standard. I understand this is internal educational material and does not confer authority.</span>
        </label>
        <button className="btn-primary" onClick={submit} data-testid="assessment-submit" disabled={!acked}>Submit assessment</button>
        {res && <div className={`mt-3 chip ${res.completed?'chip-green':'chip-amber'}`} data-testid="assessment-result">
          {res.completed ? `Lesson complete — ${res.correct}/${res.total} items answered. Next lesson unlocked.` : `${res.correct}/${res.total} items answered — please answer each item and check the acknowledgment.`}
        </div>}
      </div>}

      {!isOpen && d.quiz.length>0 && <div className="mt-8">
        <H2>Quiz</H2>
        <div className="text-xs mb-3" style={{color:'var(--muted)'}}>Pass at 80% or better to complete this lesson and unlock the next.</div>
        {d.quiz.map((q, qi) => (
          <Card key={q.id} className="mb-3">
            <div className="font-medium mb-2">{qi+1}. {q.prompt}</div>
            {q.options.map((o, oi) => (
              <label key={oi} className="flex gap-2 py-1 cursor-pointer">
                <input type="radio" name={`q${qi}`} className="w-4" checked={ans[qi]===oi} onChange={()=>{const a=[...ans]; a[qi]=oi; setAns(a);}} data-testid={`quiz-q${qi}-o${oi}`}/> {o}
              </label>
            ))}
          </Card>
        ))}
        <button className="btn-primary" onClick={submit} data-testid="quiz-submit">Submit answers</button>
        {res && <div className={`mt-3 ${res.completed?'chip-green':'chip-amber'} chip`} data-testid="quiz-result">
          Score: {res.correct}/{res.total} ({Math.round(res.score*100)}%) {res.completed?'· Passed — lesson complete':`· Below ${Math.round((res.threshold||0.8)*100)}% — retake to pass`}
        </div>}
      </div>}
      {(res?.completed || d.progress?.completed) && d.next_lesson && (
        <div className="mt-6">
          <Link to={`/academy/lesson/${d.next_lesson.id}`} className="btn-gold inline-block" data-testid="next-lesson-btn">Next lesson: {d.next_lesson.title} →</Link>
        </div>
      )}
    </div>
  );
}

// ==================== MAPS ====================
export function MapsPage() {
  const [GroveMap, setGroveMap] = useState(null);
  useEffect(() => { import("./components/GroveMap").then(m => setGroveMap(() => m.default)); }, []);
  return (
    <div data-testid="maps-page">
      <H1>Maps</H1><Sub>The living picture of the Grove — a fully interactive relationship map. Drag nodes, draw connections from the gold handles, rename, recolor, and save for every trustee.</Sub>
      {GroveMap ? <GroveMap/> : <div>Loading canvas…</div>}
      <div className="mt-3 text-xs" style={{color:'var(--muted)'}}>Drag to move · drag from a node's bottom handle to another node's top handle to connect · click to select, then rename/recolor in the panel · Delete key removes the selection · smooth animated edges available per connection.</div>
    </div>
  );
}

// ==================== MY ROOT ====================
export function MyRootPage() {
  const { data: rows, reload } = useLoader(() => A.reg.list("my_root"));
  const root = (rows||[])[0];
  const [f, setF] = useState({ HouseholdName:"", TrustName:"", Notes:"", Checklist: { "Root drafted": false, "Root reviewed": false, "Root executed": false, "Root funded": false, "709/GST filed": false } });
  useEffect(() => { if (root) setF({ HouseholdName: root.HouseholdName||"", TrustName: root.TrustName||"", Notes: root.Notes||"", Checklist: root.Checklist || f.Checklist }); }, [root]); // eslint-disable-line
  const save = async () => {
    if (root) await A.reg.patch("my_root", root.id, f);
    else await A.reg.create("my_root", f);
    toast.success("Saved to your private root"); reload();
  };
  return (
    <div data-testid="my-root-page">
      <H1>My Root</H1><Sub>Private to you. Never shared.</Sub>
      <div className="grid md:grid-cols-2 gap-3">
        <input placeholder="Household name" value={f.HouseholdName} onChange={e=>setF({...f, HouseholdName:e.target.value})}/>
        <input placeholder="Trust name" value={f.TrustName} onChange={e=>setF({...f, TrustName:e.target.value})}/>
      </div>
      <Card className="mt-4">
        <div className="label-caps mb-2">Formation Checklist</div>
        {Object.entries(f.Checklist).map(([k,v]) => (
          <label key={k} className="flex items-center gap-2 py-1"><input type="checkbox" className="w-4" checked={v} onChange={e=>setF({...f, Checklist: {...f.Checklist, [k]: e.target.checked}})}/> {k}</label>
        ))}
      </Card>
      <textarea rows={5} className="mt-4" placeholder="Private notes…" value={f.Notes} onChange={e=>setF({...f, Notes:e.target.value})}/>
      <button className="btn-primary mt-3" onClick={save}>Save</button>
    </div>
  );
}

// ==================== STRUCTURE (read-only variant of maps) ====================
export function StructurePage() {
  return (
    <div>
      <H1>Structure</H1><Sub>The Grove at a glance.</Sub>
      <Card>
        <ul className="list-disc pl-6 space-y-1 text-sm">
          <li><b>The Grove</b> — the Endigenum Hereditas Apex</li>
          <li>Five household roots (irrevocable dynasty trusts) → co-own <b>Holdings</b> (trunk)</li>
          <li><b>Grove Keeper</b> — noncharitable purpose trust holding the Guardian Share</li>
          <li>Branches: Exchange · Group · Vectura Medica · Praedium · Domus Vitae · Praesidium</li>
          <li>Affiliates: Association (PMA) · Foundation (501(c)(3), Phase 3)</li>
        </ul>
      </Card>
      <p className="text-sm mt-4" style={{color:'var(--muted)'}}>See the <Link to="/maps" className="underline">interactive Maps</Link> for the diagram.</p>
    </div>
  );
}

// ==================== CONNECT ====================
export function ConnectPage() {
  const links = [
    { t:"SharePoint site", u:"https://sharepoint.com", d:"Lists, PDFs, minutes book (system of record)."},
    { t:"Teams channel", u:"https://teams.microsoft.com", d:"Recorded meetings & chat."},
    { t:"Planner", u:"https://tasks.office.com", d:"Compliance & filing tasks."},
    { t:"Outlook", u:"https://outlook.office.com", d:"Notices & correspondence."},
    { t:"MFA setup", u:"https://aka.ms/mfasetup", d:"Enrol / manage authenticator."},
  ];
  return (
    <div data-testid="connect-page">
      <H1>Connect</H1><Sub>The portal never stores Microsoft credentials. These are plain external links.</Sub>
      <div className="grid md:grid-cols-2 gap-4">
        {links.map(l => (
          <a key={l.t} href={l.u} target="_blank" rel="noreferrer" className="card p-5 block hover:-translate-y-0.5 transition-transform">
            <div className="font-display text-xl">{l.t}</div>
            <div className="text-sm mt-1" style={{color:'var(--muted)'}}>{l.d}</div>
          </a>
        ))}
      </div>
    </div>
  );
}

// ==================== BACKUP ====================
export function BackupPage() {
  const [busy, setBusy] = useState(false);
  const [restoreText, setRestoreText] = useState("");
  const exp = async () => { setBusy(true); const r = await A.backupExport(); const blob = new Blob([JSON.stringify(r.data,null,2)],{type:'application/json'}); A.saveBlob(blob, `grove-backup-${new Date().toISOString().slice(0,10)}.json`); setBusy(false); };
  const restore = async () => { if(!window.confirm("Restore will REPLACE shared registers. Continue?")) return; try { await A.backupRestore(JSON.parse(restoreText)); toast.success("Restored"); } catch(e){ toast.error("Failed: "+e.message);} };
  return (
    <div>
      <H1>Backup & Restore</H1><Sub>The record's parachute. Export quarterly and file offline.</Sub>
      <Card className="mb-4"><div className="label-caps mb-2">Export</div>
        <button className="btn-primary" onClick={exp} disabled={busy} data-testid="backup-export">{busy?"…":"Download JSON backup"}</button></Card>
      <Card><div className="label-caps mb-2">Restore (admin only)</div>
        <textarea rows={5} placeholder="Paste backup JSON…" value={restoreText} onChange={e=>setRestoreText(e.target.value)}/>
        <button className="btn-danger mt-2" onClick={restore}>Restore</button></Card>
    </div>
  );
}

// ==================== INTEGRITY ====================
export function IntegrityPage() {
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const scan = async () => { setBusy(true); const r = await A.integrityScan(); setRes(r.data); setBusy(false); };
  return (
    <div>
      <H1>Integrity Scan</H1><Sub>A one-click audit. Findings only — no automatic changes.</Sub>
      <button className="btn-primary" onClick={scan} disabled={busy} data-testid="scan-run">{busy?"Scanning…":"Run integrity scan"}</button>
      {res && (<Card className="mt-6">
        <div className="label-caps mb-2">Findings ({res.findings.length}) · {res.when.slice(0,16).replace('T',' ')}</div>
        {res.findings.length===0 && <div className="text-sm">Clean.</div>}
        <ul className="list-disc pl-6 space-y-1 text-sm">{res.findings.map((f,i)=>(<li key={i}>{f}</li>))}</ul>
      </Card>)}
    </div>
  );
}

// ==================== ACCESS & ADMINISTRATION (Trustee Administrator console) ====================
const STATUS_CHIP = {
  approved: ["Active", "green"], active: ["Active", "green"],
  submitted: ["Submitted", "amber"], under_review: ["Under review", "amber"],
  info_requested: ["Info requested", "gold"], pending: ["Pending", "amber"],
  suspended: ["Suspended", "red"], revoked: ["Revoked", "red"],
  denied: ["Denied", "red"], expired: ["Expired", "gray"], archived: ["Archived", "gray"],
};

export function UsersPage() {
  const { data: users, reload } = useLoader(A.listUsers);
  const [roles, setRoles] = useState([]);
  const [smith, setSmith] = useState(null);
  const [roleModal, setRoleModal] = useState(null); // {user}
  const [confirmPw, setConfirmPw] = useState("");
  const [pickRole, setPickRole] = useState("");
  const [pickAdmin, setPickAdmin] = useState(false);
  const [temp, setTemp] = useState(null); // {email, temp_password}

  useEffect(() => {
    A.admin.portalRoles().then(r => setRoles(r.data.roles)).catch(() => {});
    A.admin.smithReport().then(r => setSmith(r.data)).catch(() => {});
  }, []);

  const act = async (fn, msg) => { try { await fn(); toast.success(msg); reload(); } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } };
  const approve = (id) => act(() => A.approveUser(id), "Approved");
  const deny = (id) => act(() => A.denyUser(id, "Denied by administrator"), "Denied");
  const suspend = (id) => act(() => A.admin.suspend(id), "Suspended — sessions revoked");
  const reactivate = (id) => act(() => A.admin.reactivate(id), "Reactivated");
  const revoke = (id) => act(() => A.admin.revokeSessions(id), "All sessions revoked");
  const requestInfo = (id) => { const n = prompt("What information do you need from the applicant?"); if (n) act(() => A.admin.requestInfo(id, n), "Info requested"); };
  const issueTemp = async (u) => {
    if (!window.confirm(`Issue a fresh forced-reset temp password for ${u.email}?`)) return;
    try { const r = await A.admin.issueTempPassword(u.id); setTemp(r.data); reload(); } catch (e) { toast.error("Failed"); }
  };
  const openRole = (u) => { setRoleModal({ user: u }); setPickRole(u.portal_role || ""); setPickAdmin(!!u.is_admin); setConfirmPw(""); };
  const saveRole = async () => {
    try {
      await A.admin.setRole(roleModal.user.id, { portal_role: pickRole || null, is_admin: pickAdmin, confirm_password: confirmPw });
      toast.success("Role updated"); setRoleModal(null); reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Reauthentication failed"); }
  };

  const list = users || [];
  const requests = list.filter(u => ["submitted", "under_review", "info_requested", "pending"].includes(u.status));
  const chip = (s) => { const [label, tone] = STATUS_CHIP[s] || [s, "gray"]; return <span className={`chip chip-${tone}`}>{label}</span>; };

  return (
    <div data-testid="users-page">
      <H1>Access & Administration</H1>
      <Sub>Trustee Administrator control — review access requests, approve or deny, manage roles and permissions, suspend or revoke sessions, and issue forced-reset passwords. Every action is logged.</Sub>

      {smith && (
        <Card className="mb-6" data-testid="smith-report-card">
          <div className="font-display text-lg mb-2">Trustee Administrator</div>
          <div className="grid md:grid-cols-4 gap-3 text-sm">
            <div><div className="label-caps">Account</div>{smith.login_email}</div>
            <div><div className="label-caps">Portal role</div>{smith.assigned_portal_role}</div>
            <div><div className="label-caps">Contact email</div>{smith.contact_email}</div>
            <div><div className="label-caps">Duplicate check</div>{smith.smith_account_count === 1 ? <span className="chip chip-green">Single account</span> : <span className="chip chip-red">{smith.smith_account_count} found</span>}</div>
          </div>
        </Card>
      )}

      <H2>Access requests <span className="text-sm" style={{ color: 'var(--muted)' }}>({requests.length})</span></H2>
      {requests.length === 0 ? <Card className="mb-6"><div className="text-sm" style={{ color: 'var(--muted)' }}>No pending access requests.</div></Card> : (
        <div className="space-y-3 mb-8">
          {requests.map(u => (
            <Card key={u.id} data-testid={`request-${u.id}`}>
              <div className="flex justify-between items-start gap-3">
                <div>
                  <div className="font-medium" style={{ color: 'var(--primary)' }}>{u.name} · <span className="text-xs">{u.email}</span> {chip(u.status)}</div>
                  <div className="text-xs mt-1" style={{ color: 'var(--muted)' }}>
                    Requested role: {u.access_request?.requested_role || "—"} · Affiliation: {u.household || u.access_request?.affiliation || "—"}
                  </div>
                  {u.access_request?.business_reason && <div className="text-sm mt-1">Reason: {u.access_request.business_reason}</div>}
                  {u.access_request?.admin_notes && <div className="text-xs mt-1" style={{ color: 'var(--gold-hover)' }}>Note to applicant: {u.access_request.admin_notes}</div>}
                </div>
                <div className="flex gap-1 shrink-0 flex-wrap justify-end">
                  <button className="btn-gold text-xs" onClick={() => approve(u.id)} data-testid={`approve-${u.id}`}>Approve</button>
                  <button className="btn-outline text-xs" onClick={() => requestInfo(u.id)} data-testid={`info-${u.id}`}>Request info</button>
                  <button className="btn-outline text-xs" onClick={() => deny(u.id)} data-testid={`deny-${u.id}`}>Deny</button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      <H2>All users <span className="text-sm" style={{ color: 'var(--muted)' }}>({list.length})</span></H2>
      <Card className="p-0 overflow-x-auto">
        <table><thead><tr><th>Name</th><th>Email</th><th>Portal role</th><th>Status</th><th>Actions</th></tr></thead>
          <tbody>{list.map(u => (
            <tr key={u.id} data-testid={`user-row-${u.id}`}>
              <td>{u.name} {u.is_admin && <span className="chip chip-gold text-xs ml-1">Admin</span>}</td>
              <td className="text-xs">{u.email}</td>
              <td className="text-xs">{u.portal_role || (u.role === "records_officer" ? "Records Officer" : "Trustee")}</td>
              <td>{chip(u.status)}</td>
              <td>
                <div className="flex gap-1 flex-wrap">
                  <button className="btn-outline text-xs" onClick={() => openRole(u)} data-testid={`role-${u.id}`}>Role</button>
                  {["approved", "active"].includes(u.status)
                    ? <button className="btn-outline text-xs" onClick={() => suspend(u.id)} data-testid={`suspend-${u.id}`}>Suspend</button>
                    : ["suspended", "denied", "revoked"].includes(u.status) && <button className="btn-outline text-xs" onClick={() => reactivate(u.id)} data-testid={`reactivate-${u.id}`}>Reactivate</button>}
                  <button className="btn-outline text-xs" onClick={() => revoke(u.id)} data-testid={`revoke-${u.id}`}>Revoke sessions</button>
                  <button className="btn-outline text-xs" onClick={() => issueTemp(u)} data-testid={`temp-${u.id}`}>Temp pw</button>
                </div>
              </td>
            </tr>))}</tbody></table>
      </Card>

      {roleModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50" onClick={() => setRoleModal(null)}>
          <div className="card p-6 w-full max-w-md" onClick={e => e.stopPropagation()} data-testid="role-modal">
            <div className="font-display text-xl mb-1">Set role — {roleModal.user.name}</div>
            <div className="text-xs mb-4" style={{ color: 'var(--muted)' }}>Critical action — confirm your password to proceed.</div>
            <label className="label-caps">Portal role</label>
            <select value={pickRole} onChange={e => setPickRole(e.target.value)} className="mb-3" data-testid="role-select">
              <option value="">— none —</option>
              {roles.map(r => <option key={r} value={r}>{r}</option>)}
            </select>
            <label className="flex items-center gap-2 text-sm mb-3"><input type="checkbox" className="w-auto" checked={pickAdmin} onChange={e => setPickAdmin(e.target.checked)} data-testid="role-admin"/> Grant Trustee Administrator (admin) capability</label>
            <input type="password" placeholder="Confirm your password" value={confirmPw} onChange={e => setConfirmPw(e.target.value)} className="mb-4" data-testid="role-confirm-pw"/>
            <div className="flex gap-2">
              <button className="btn-primary" onClick={saveRole} data-testid="role-save">Save</button>
              <button className="btn-outline" onClick={() => setRoleModal(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {temp && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50" onClick={() => setTemp(null)}>
          <div className="card p-6 w-full max-w-md" onClick={e => e.stopPropagation()} data-testid="temp-modal">
            <div className="font-display text-xl mb-2">Temporary password issued</div>
            <div className="banner-warning mb-3 text-xs">Share this securely with {temp.email}. They must set a new password on first login. This is shown only once.</div>
            <div className="font-mono text-lg p-3 rounded text-center" style={{ background: 'var(--surface-2)' }} data-testid="temp-value">{temp.temp_password}</div>
            <button className="btn-primary mt-4 w-full" onClick={() => setTemp(null)}>Done</button>
          </div>
        </div>
      )}
    </div>
  );
}


// ==================== LOGIN / SIGNUP ====================
export function LoginPage() {
  const nav = useNavigate();
  const [tab, setTab] = useState("login");
  const [f, setF] = useState({ email:"", password:"", name:"", household:"", requested_role:"", business_reason:"", confidentiality_ack:false });
  const [reset, setReset] = useState(null); // { current: "temp" } if force reset required
  const [newPw, setNewPw] = useState(""); const [newPw2, setNewPw2] = useState("");
  const submit = async () => {
    try {
      if (tab==="login") {
        const r = await A.login(f.email, f.password);
        localStorage.setItem("ehu_token", r.data.token);
        if (r.data.must_change_password) {
          setReset({ current: f.password });
        } else {
          nav("/dashboard");
        }
      } else {
        if (!f.confidentiality_ack) return toast.error("Please accept the confidentiality & internal-use terms");
        await A.signup(f); toast.success("Access request submitted. Awaiting administrator review."); setTab("login");
      }
    } catch(e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const doReset = async () => {
    if (newPw.length < 8) return toast.error("New password must be at least 8 characters");
    if (newPw !== newPw2) return toast.error("Passwords don't match");
    try { await A.changePassword(reset.current, newPw); toast.success("Password updated"); nav("/dashboard"); }
    catch(e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  if (reset) return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{background:'var(--bg)'}}>
      <div className="w-full max-w-md card p-8" data-testid="force-reset">
        <div className="font-display text-2xl mb-2">Set a new password</div>
        <div className="banner-warning mb-4">This is your first sign-in. Please replace the temporary password before continuing.</div>
        <input type="password" placeholder="New password (min 8 chars)" value={newPw} onChange={e=>setNewPw(e.target.value)} className="mb-2" data-testid="new-pw"/>
        <input type="password" placeholder="Confirm new password" value={newPw2} onChange={e=>setNewPw2(e.target.value)} className="mb-4" data-testid="new-pw2"/>
        <button className="btn-primary w-full" onClick={doReset} data-testid="reset-submit">Set password & continue</button>
      </div>
    </div>
  );
  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{background:'var(--bg)'}}>
      <div className="w-full max-w-md card p-8">
        <div className="font-display text-3xl">Endigenum Hereditas</div>
        <div className="text-sm mb-6" style={{color:'var(--gold-hover)'}}>Apex Registry — trustee cockpit</div>
        <div className="flex gap-2 mb-4">
          <button className={tab==="login"?"btn-primary":"btn-outline"} onClick={()=>setTab("login")} data-testid="tab-login">Sign in</button>
          <button className={tab==="signup"?"btn-primary":"btn-outline"} onClick={()=>setTab("signup")} data-testid="tab-signup">Request access</button>
        </div>
        {tab==="signup" && (<>
          <input placeholder="Full name" value={f.name} onChange={e=>setF({...f,name:e.target.value})} className="mb-2" data-testid="signup-name"/>
          <input placeholder="Household / affiliation" value={f.household} onChange={e=>setF({...f,household:e.target.value})} className="mb-2"/>
          <input placeholder="Requested role (e.g. Beneficiary, Bookkeeper)" value={f.requested_role} onChange={e=>setF({...f,requested_role:e.target.value})} className="mb-2" data-testid="signup-role"/>
          <textarea placeholder="Business reason for access" value={f.business_reason} onChange={e=>setF({...f,business_reason:e.target.value})} className="mb-2" rows={2}/>
        </>)}
        <input placeholder="email" value={f.email} onChange={e=>setF({...f,email:e.target.value})} className="mb-2" data-testid="login-email"/>
        <input type="password" placeholder="password" value={f.password} onChange={e=>setF({...f,password:e.target.value})} className="mb-4" data-testid="login-password"/>
        {tab==="signup" && (
          <label className="flex items-start gap-2 text-xs mb-4" style={{color:'var(--muted)'}} data-testid="signup-ack-label">
            <input type="checkbox" className="w-auto mt-0.5" checked={f.confidentiality_ack} onChange={e=>setF({...f,confidentiality_ack:e.target.checked})} data-testid="signup-ack"/>
            <span>I acknowledge these records are strictly confidential and for authorized internal use only. Access is granted only after administrator review — submitting this request does not grant access.</span>
          </label>
        )}
        <button className="btn-gold w-full" onClick={submit} data-testid="submit-auth">{tab==="login"?"Sign in":"Request access"}</button>
        {tab==="signup" && <div className="text-xs mt-3" style={{color:'var(--muted)'}}>New accounts require administrator approval.</div>}
      </div>
    </div>
  );
}
